import React from 'react';
import type {
  DropdownIndicatorProps,
  MenuPlacement,
  SingleValueProps,
} from 'react-select';
import { components } from 'react-select';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import Caret from '../../components/Caret';
import Select from '../../components/Select';
import SVGIcon from '../../components/SVGIcon';
import { PostLinkAccess } from '../../graphql/generated/graphql';
import checkIcon from '../../icons/check.svg';
import linkIcon from '../../icons/link.svg';
import publicIcon from '../../icons/public.svg';
import removeIcon from '../../icons/remove.svg';
import teamIcon from '../../icons/team.svg';
import type { SelectOptionType } from '../../components/SelectOption';
import SelectOption from '../../components/SelectOption';
import isSize from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import postPath from '../../utils/url/postPath';
import { usePublicContentPermissions } from '../../utils/useAllowPublicContentBy';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import { useUpdatePostLinkAccess } from './postPermissionOperations';
import styles from './SharingDropdownLink.module.scss';
import fullOrgUrl from '../../utils/url/fullOrgUrl';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const SHARING_DROPDOWN_LINK_POST_FRAGMENT = gql(`
  fragment SharingDropdownLink_post on Post {
    id
    linkAccess
    publishedAt
    ...usePostPolicyCheck_post
    ...useUpdatePostLinkAccess_post
    ...postPath_post
  }
`);

const DropdownIndicator = <
  OptionType = unknown,
  IsMulti extends boolean = false,
>(
  props: DropdownIndicatorProps<OptionType, IsMulti>,
) => {
  return (
    <div className={styles.dropdownIndicator}>
      <Caret direction={props.selectProps.menuIsOpen ? 'up' : 'down'} />
    </div>
  );
};

const SingleValue = <OptionType = unknown, IsMulti extends boolean = false>({
  children,
  ...props
}: SingleValueProps<OptionType, IsMulti>) => (
  <components.SingleValue {...props}>
    <div className={styles.accessContainer}>
      <div className={styles.secondary}>Link access: </div>
      <div className={styles.selectValue}>{children}</div>
    </div>
  </components.SingleValue>
);

interface SharingDropdownLinkProps {
  post: DocumentType<typeof SHARING_DROPDOWN_LINK_POST_FRAGMENT>;
  initialMenuIsOpen?: boolean;
}

const SharingDropdownLink = ({
  post,
  post: { linkAccess, publishedAt },
  initialMenuIsOpen,
}: SharingDropdownLinkProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const postPolicyCheck = usePostPolicyCheck();
  const canEdit = postPolicyCheck(PostPolicyAction.EditPost, post, currentUser);
  const { isPermittedToPublicizePost } = usePublicContentPermissions();

  const updateLinkAccess = useUpdatePostLinkAccess();

  const [isCopied, setIsCopied] = React.useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(fullOrgUrl(postPath(post)));
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1500);
  };

  const isDisabled = !canEdit;

  const options: SelectOptionType<PostLinkAccess>[] = [
    ...(publishedAt
      ? []
      : [
          {
            value: PostLinkAccess.Disabled,
            icon: removeIcon,
            label: 'Disabled',
            helpText: 'Only the users above can access this post',
            isDisabled,
          },
        ]),
    {
      value: PostLinkAccess.Internal,
      icon: publishedAt ? teamIcon : linkIcon,
      label: publishedAt ? 'Internal' : 'Direct',
      helpText: publishedAt
        ? 'Users and members above can access this post'
        : 'Any teammate with this link can access this post.',
      isDisabled,
    },
    {
      value: PostLinkAccess.Public,
      icon: publicIcon,
      label: 'Public',
      helpText: 'Anyone on the internet with this link can access this post.',
      isDisabled,
      ...(!isPermittedToPublicizePost
        ? {
            isDisabled: true,
            tooltip: 'Only admins can make posts public',
            tooltipPlacement: 'bottom',
          }
        : {}),
    },
  ];

  const presentedLinkAccess =
    linkAccess === PostLinkAccess.PublicEdit
      ? PostLinkAccess.Public
      : linkAccess;

  return (
    <div className={styles.container}>
      {isCopied ? (
        <Button
          icon={
            <SVGIcon className={styles.check} size="small">
              {checkIcon}
            </SVGIcon>
          }
          variant="muted"
          size="small"
          disabled
        >
          Copied!
        </Button>
      ) : (
        <Button
          icon={linkIcon}
          onClick={handleCopy}
          variant="link"
          size="small"
        >
          Copy link
        </Button>
      )}
      <Select
        menuPlacement={(isSize(Sizes.SM) ? 'top' : 'auto') as MenuPlacement}
        onChange={(value) =>
          value !== presentedLinkAccess ? updateLinkAccess(post, value) : null
        }
        className={styles.select}
        isSearchable={false}
        options={options}
        value={presentedLinkAccess}
        menuIsOpen={initialMenuIsOpen}
        components={{
          DropdownIndicator: canEdit ? DropdownIndicator : null,
          Option: SelectOption<PostLinkAccess>,
          SingleValue,
        }}
      />
    </div>
  );
};

export default SharingDropdownLink;
